import React, { useState, useEffect } from 'react';
import { useLanguage } from '../LanguageContext';
import { User } from '../types';
import './ArtistDashboard.css';

interface Song {
  id: number;
  title: string;
  artistName: string;
  coverUrl: string;
  audioUrl: string;
  spotifyUrl: string;
  uploadId: string;
  status: string;
  lanes: number;
  detectedBpm: number | null;
  createdAt: string;
}

interface ArtistDashboardProps {
  authToken: string;
  user: User;
  onLogout: () => void;
  onProfile?: () => void;
}

const ArtistDashboard: React.FC<ArtistDashboardProps> = ({ authToken, user, onLogout, onProfile }) => {
  const { t } = useLanguage();
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState<'url' | 'file'>('url');
  const [form, setForm] = useState({
    title: '',
    artistName: user.nickname || user.username || user.email.split('@')[0],
    coverUrl: '',
    audioUrl: '',
    spotifyUrl: '',
    lanes: 2,
  });

  useEffect(() => {
    fetchSongs();
  }, []);

  const fetchSongs = async () => {
    try {
      const res = await fetch('/api/artist/songs', {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      setSongs(data);
    } catch (err) {
      console.error('Failed to fetch songs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);

    try {
      const res = await fetch('/api/artist/songs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          title: form.title,
          artistName: form.artistName,
          coverUrl: form.coverUrl || null,
          audioUrl: form.audioUrl || null,
          spotifyUrl: form.spotifyUrl,
          lanes: form.lanes,
        }),
      });

      const song = await res.json();

      if (form.audioUrl) {
        await fetch(`/api/artist/songs/${song.id}/generate-beats`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({}),
        });
      }

      setShowUpload(false);
      setForm({
        title: '',
        artistName: user.nickname || user.username || user.email.split('@')[0],
        coverUrl: '',
        audioUrl: '',
        spotifyUrl: '',
        lanes: 2,
      });
      fetchSongs();
    } catch (err) {
      console.error('Failed to create song:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (songId: number) => {
    if (!window.confirm(t.artist.confirmDelete)) return;

    try {
      await fetch(`/api/artist/songs/${songId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      fetchSongs();
    } catch (err) {
      console.error('Failed to delete song:', err);
    }
  };

  const handleGenerateBeats = async (songId: number) => {
    try {
      await fetch(`/api/artist/songs/${songId}/generate-beats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({}),
      });
      fetchSongs();
    } catch (err) {
      console.error('Failed to generate beats:', err);
    }
  };

  const handleRequestUpload = async (songId: number, type: 'audio' | 'cover') => {
    try {
      const res = await fetch('/api/artist/request-upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ songId, type }),
      });
      const data = await res.json();
      if (data.uploadUrl) {
        window.open(data.uploadUrl, '_blank');
      }
    } catch (err) {
      console.error('Failed to request upload:', err);
    }
  };

  const LanesVisual = ({ lanes }: { lanes: number }) => (
    <div className="lanes-visual" title={t.artist.lanesDesc}>
      {Array.from({ length: lanes }).map((_, i) => (
        <div key={i} className="lane-dot" />
      ))}
    </div>
  );

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="dashboard-logo">
          <div className="logo-icon">🎧</div>
          <div>
            <h1>{t.artist.dashboard}</h1>
            <p>{user.nickname || user.email}</p>
          </div>
        </div>
        <div className="header-actions">
          {onProfile && (
            <button className="btn btn-outline btn-sm" onClick={onProfile}>
              ⚙️
            </button>
          )}
          <button className="btn btn-outline" onClick={onLogout}>
            {t.common.logout}
          </button>
        </div>
      </header>

      <main className="dashboard-content">
        <div className="dashboard-actions">
          <h2>{t.artist.mySongs}</h2>
          <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
            + {t.artist.addSong}
          </button>
        </div>

        {loading ? (
          <div className="loading">{t.common.loading}</div>
        ) : songs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎵</div>
            <h3>{t.songs.noSongs}</h3>
            <p>Upload your first song to get started!</p>
          </div>
        ) : (
          <div className="songs-grid">
            {songs.map((song) => (
              <div key={song.id} className="song-card-dash">
                <div className="song-cover-dash">
                  {song.coverUrl ? (
                    <img src={song.coverUrl} alt={song.title} />
                  ) : (
                    <div className="cover-placeholder">🎵</div>
                  )}
                  {!song.coverUrl && song.uploadId && (
                    <button
                      className="upload-overlay-btn"
                      onClick={() => handleRequestUpload(song.id, 'cover')}
                    >
                      📷 {t.artist.uploadCover}
                    </button>
                  )}
                </div>
                <div className="song-info-dash">
                  <h3>{song.title}</h3>
                  <p>{song.artistName}</p>
                  <div className="song-meta">
                    <LanesVisual lanes={song.lanes} />
                    {song.detectedBpm && (
                      <span className="bpm-badge">{song.detectedBpm} BPM</span>
                    )}
                  </div>
                  <span className={`status status-${song.status}`}>
                    {song.status === 'active' ? t.artist.active : t.artist.pending}
                  </span>
                  {song.uploadId && (
                    <span className="upload-id">ID: {song.uploadId}</span>
                  )}
                </div>
                <div className="song-actions-dash">
                  {!song.audioUrl && song.uploadId && (
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleRequestUpload(song.id, 'audio')}
                    >
                      🎵 {t.artist.uploadAudio}
                    </button>
                  )}
                  {song.status === 'pending' && song.audioUrl && (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleGenerateBeats(song.id)}
                    >
                      {t.artist.generateBeats}
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(song.id)}
                  >
                    {t.artist.delete}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {showUpload && (
        <div className="modal-overlay" onClick={() => setShowUpload(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t.artist.addSong}</h2>
              <button className="modal-close" onClick={() => setShowUpload(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="upload-form">
              <div className="form-group">
                <label>{t.artist.title} *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="My Awesome Track"
                  required
                />
              </div>

              <div className="form-group">
                <label>{t.artist.artistName}</label>
                <input
                  type="text"
                  value={form.artistName}
                  onChange={(e) => setForm({ ...form, artistName: e.target.value })}
                  placeholder="Your artist name"
                />
              </div>

              <div className="form-group">
                <label className="label-with-toggle">
                  <span>{t.artist.audioUrl}</span>
                  <div className="toggle-pills">
                    <button
                      type="button"
                      className={`pill ${uploadMode === 'url' ? 'active' : ''}`}
                      onClick={() => setUploadMode('url')}
                    >
                      URL
                    </button>
                    <button
                      type="button"
                      className={`pill ${uploadMode === 'file' ? 'active' : ''}`}
                      onClick={() => setUploadMode('file')}
                    >
                      Upload
                    </button>
                  </div>
                </label>
                {uploadMode === 'url' ? (
                  <input
                    type="url"
                    value={form.audioUrl}
                    onChange={(e) => setForm({ ...form, audioUrl: e.target.value })}
                    placeholder="https://example.com/song.mp3"
                  />
                ) : (
                  <div className="file-upload-note">
                    <p>Leave blank now. After creating the song, use the upload button to upload via webhook.</p>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>{t.artist.coverUrl}</label>
                <input
                  type="url"
                  value={form.coverUrl}
                  onChange={(e) => setForm({ ...form, coverUrl: e.target.value })}
                  placeholder="https://example.com/cover.jpg"
                />
                <span className="form-hint">Leave blank to upload later via webhook</span>
              </div>

              <div className="form-group">
                <label>{t.artist.spotifyUrl}</label>
                <input
                  type="url"
                  value={form.spotifyUrl}
                  onChange={(e) => setForm({ ...form, spotifyUrl: e.target.value })}
                  placeholder="https://open.spotify.com/track/..."
                />
              </div>

              <div className="form-group lanes-group">
                <label>
                  {t.artist.lanes}
                  <span className="lanes-help" title={t.artist.lanesDesc}>ℹ️</span>
                </label>
                <div className="lanes-selector">
                  {[1, 2, 3, 4].map((num) => (
                    <button
                      key={num}
                      type="button"
                      className={`lane-option ${form.lanes === num ? 'active' : ''}`}
                      onClick={() => setForm({ ...form, lanes: num })}
                    >
                      <div className="lane-preview">
                        {Array.from({ length: num }).map((_, i) => (
                          <div key={i} className="lane-bar" />
                        ))}
                      </div>
                      <span>{num}</span>
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={uploading}
              >
                {uploading ? t.common.loading : t.artist.addSong}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistDashboard;
